<template>
  <NormalHeaderLayout header="Methodology">
    <h2>Types of data and representation</h2>
    <p>Two principal types of data are being collected:</p>
    <ol>
      <li>Direct products of a specific economy, ASN, or ASN-in-an-Economy as a category.</li>
      <li>Combinations of economies, to produce a region or collection.</li>
    </ol>
    <p>Static files reflect a daily production cycle. Every day, the prior day's raw HTTP logs are processed to calculate (for example) the visible rate of IPv6 per ASN and per economy, and the samplecount of that day, also per economy or ASN. These are the primary inputs.</p>
    <h2>
      Both kinds of data can be
      <i>smoothed</i> or
      <i>raw</i>
    </h2>
    <ol>
      <li>
        <i>Smoothed</i> data has a simple (linear) average applied over the time interval. So we have 10, 30, 60 and 90 day smoothing.
      </li>
      <li>
        <i>Raw</i> data is the simple count of events (or a computed percentage for some attribute) collected in the 24h period.
      </li>
    </ol>
    <p>
      Using the
      <i>raw</i> data, any smoothing, (for example exponentially-weighted moving average) can be calculated. The pre-calculated smoothing intervals represent reasonable analogues for "a week", "a month", "a couple of months", and "a quarter (3 months)" data. Random variances are made clear.
    </p>
    <p>
      The
      <i>smoothed</i> data sources do not cope well with rapid changes in status. So (for example) a deployment of IPv6 which is completed in under a week is represented as a linear rate of change over a month or a 3 month window. Exponentially weighted smoothing would cope with this kind of rapid change better. On the plus side, random variances are minimized.
    </p>
    <h2>Combinations can be the simple sum, or a weighted sum</h2>
    <p>Some data is collated across multiple keys.</p>
    <p>For instance, data about AS1221 (Telstra) represents an entity known to be in Australia (AU) and in the Oceania region, and also in the Asia-Pacific (APNIC) economies, and the OECD.</p>
    <p>Simple sums can be derived by summing for the key in question. So data about Oceania can be computed using all sources in Oceania, irrespective of what we think is sampling bias between sources in Oceania.</p>
    <h2>Calculating the weighted sum</h2>
    <p>Data (for example from the random advert placement) can be weighted back into an approximation of expected ratio per economy based on a curated model.</p>
    <p>Separately, an APNIC Labs model of population is being maintained (it's a crude model - basically it projects from international census data) which can be used to derive the ratios between economies.</p>
    <p>From these ratios, the actual sample counts per economy and the list of economies and ratios, derives an "adjustment" figure per day. It has to be each day, because the per-day variances for advertising presentation can be quite large.</p>
    <h3>Example weighting method</h3>
    <p>For example, imagine a hypothetical world:</p>
    <table>
      <tr>
        <td>China:</td>
        <td>1,000,000,000</td>
        <td>users</td>
      </tr>
      <tr>
        <td>India:</td>
        <td>1,000,000,000</td>
        <td>users</td>
      </tr>
      <tr>
        <td>USA:</td>
        <td>250,000,000</td>
        <td>users</td>
      </tr>
      <tr>
        <td>Brazil:</td>
        <td>250,000,000</td>
        <td>users</td>
      </tr>
      <tr>
        <td>Europe:</td>
        <td>500,000,000</td>
        <td>users</td>
      </tr>
      <tr>
        <td colspan="3">-------------------------</td>
      </tr>
      <tr>
        <td>Total:</td>
        <td>3,000,000,000</td>
        <td>users</td>
      </tr>
    </table>
    <p>These are in the inter-economy ratio of:</p>
    <p>1 : 1 : 0.25 : 0.25 : 0.5</p>
    <p>But expressed as a fraction of the total population, we would say:</p>
    <p>1/3 : 1/3 : 1/12 : 1/12 : 1/6</p>
    <p>(and if you look at these as inter-economy ratios, they have the same relative scale to each other).</p>
    <p>On some given day, we see the following samples:</p>

    <table>
      <tr>
        <td>China:</td>
        <td>200,000</td>
        <td>samples</td>
        <td>0.25</td>
        <td>v6</td>
        <td>50,000</td>
      </tr>
      <tr>
        <td>India:</td>
        <td>50,000</td>
        <td>samples</td>
        <td>0.50</td>
        <td>v6</td>
        <td>25,000</td>
      </tr>
      <tr>
        <td>USA:</td>
        <td>200,000</td>
        <td>samples</td>
        <td>0.40</td>
        <td>v6</td>
        <td>80,000</td>
      </tr>
      <tr>
        <td>Brazil:</td>
        <td>50,000</td>
        <td>samples</td>
        <td>0.20</td>
        <td>v6</td>
        <td>10,000</td>
      </tr>
      <tr>
        <td>Europe:</td>
        <td>500,000</td>
        <td>samples</td>
        <td>0.20</td>
        <td>v6</td>
        <td>100,000</td>
      </tr>
      <tr>
        <td colspan="6">--------------------------------------------------</td>
      </tr>
      <tr>
        <td>Total:</td>
        <td>1,000,000</td>
        <td>samples</td>
        <td></td>
        <td></td>
        <td>265,000</td>
      </tr>
    </table>
    <p>Which is 26.5% v6 as a simple total over the samples 265,000/1,000,000.</p>
    <p>We saw 1 million samples, but the inter-economy ratio was:</p>
    <p>1 : 0.25 : 1 : 0.25 : 2.5</p>
    <p>Expressed as a fraction of the whole sample count, we would say:</p>
    <p>1/5 : 1/20 : 1/5 : 1/20 : 1/2</p>
    <p>Viewed against the sample count, the entire sample is skewed. We need to adjust every count to get back to the balance we expected from each economy.</p>
    <p>To correct for this, we have to multiply the sample behaviours by the adjustment factor for this day, to restore the inter-economy ratios to the one we need:</p>
    <p>The adjustment is the new ratio of the actual to desired ratios in the day.</p>

    <table>
      <tr>
        <td>China:</td>
        <td>1/(3/5)</td>
        <td>200,000</td>
        <td>=</td>
        <td>333,320</td>
        <td>at 0.25 is</td>
        <td>83,330</td>
      </tr>
      <tr>
        <td>India:</td>
        <td>1/(3/20)</td>
        <td>50,000</td>
        <td>=</td>
        <td>333,320</td>
        <td>at 0.50 is</td>
        <td>166,660</td>
      </tr>
      <tr>
        <td>USA:</td>
        <td>1/(12/5)</td>
        <td>200,000</td>
        <td>=</td>
        <td>333,320</td>
        <td>at 0.40 is</td>
        <td>33,328</td>
      </tr>
      <tr>
        <td>Brazil:</td>
        <td>1/(12/20)</td>
        <td>50,000</td>
        <td>=</td>
        <td>82,320</td>
        <td>at 0.20 is</td>
        <td>16,664</td>
      </tr>
      <tr>
        <td>Europe:</td>
        <td>1/(6/2)</td>
        <td>500,000</td>
        <td>=</td>
        <td>166,650</td>
        <td>at 0.20 is</td>
        <td>33,330</td>
      </tr>
      <tr>
        <td colspan="7">--------------------------------------------------</td>
      </tr>
      <tr>
        <td>Total:</td>
        <td></td>
        <td>1,000,000</td>
        <td>=</td>
        <td>999,950</td>
        <td></td>
        <td>333,312</td>
      </tr>
    </table>
    <p>The 999,950 adjusted samples is 1,000,000 with rounding error. So after we apply the v6 capability seen in each sample pool we get a simple total of 333,312 which is 33.3% adjusted capability.</p>
    <p>
      The adjustment factors put the
      <i>relative</i> contribution of the economies back into scale. simple arithmetic mis-calculates the v6 percentage significantly.
    </p>
    <p>This allows us to re-compute the effective total % IPv6 figure, by re-calculating the samples against the new totals per economy, but with the % v6 figure from the original data.</p>
    <h2>Structure of the products</h2>
    <p>The outputs are JSON files in a simple file tree, typically fetched via a webserver which maps URL requests to the files.</p>
    <p>Most URL paths follow the ISO 3166 economy naming convention with locally defined addendum for UN regions. A small curated collection of organisations are also defined as sets of economies (the RIR, the OECD, like entities)</p>
    <h3>Regions (additional codes for ISO 3166)</h3>
    <p>The regions data comes from a single PostgreSQL database state, which represents the UN regional data. The num value is the UN (sub)region index, which is part of the formal name of the region in the UN region model. We disregard this.</p>
    <p>Geoff Huston assigned 2-letter codes from the unallocated codeset in ISO3166 as follows:</p>
    <p>_num | cc_cc | cc_name --------+-------+--------------------------- 154 | QM | Northern Europe 39 | QN | Southern Europe 155 | QO | Western Europe 53 | QP | Australia and New Zealand 54 | QQ | Melanesia 57 | QR | Micronesia 61 | QS | Polynesia 1 | XA | World 2 | XB | Africa 19 | XC | Americas 142 | XD | Asia 150 | XE | Europe 9 | XF | Oceania 990 | XG | Unclassified 14 | XH | Eastern Africa 17 | XI | Middle Africa 15 | XJ | Northern Africa 18 | XK | Southern Africa 11 | XL | Western Africa 29 | XN | Caribbean 13 | XO | Central America 5 | XP | South America 21 | XQ | Northern America 143 | XR | Central Asia 30 | XS | Eastern Asia 34 | XT | Southern Asia 35 | XU | South Eastern Asia 145 | XV | Western Asia 151 | XW | Eastern Europe</p>
  </NormalHeaderLayout>
</template>

<script>
import NormalHeaderLayout from '../components/layout/NormalHeaderLayout';

export default {
  components: {
    NormalHeaderLayout
  }
};
</script>

<style scoped lang="scss">
ol {
  margin-left: 2.5rem;
}
td {
  padding: 3px;
  text-align: right;
}
</style>
